import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api } from '@/api/client';

interface Line { ref: string; date: string }
interface ResultRow {
  ref: string; ok: boolean; error?: string; orderNo?: string | null; merchant?: string | null;
  before?: string | null; after?: string; unchanged?: boolean; freightMoved?: number; freightInvoiced?: string[];
  periodInvoice?: string | null;
}
interface Result { apply: boolean; total: number; toChange: number; changed: number; unchanged: number; failed: number; rows: ResultRow[] }

/**
 * Paste "LR-or-AWB  date" lines — straight out of Excel (tab), or comma / space separated. A header
 * line, or any line whose first cell has no digit, is skipped.
 */
function parseLines(text: string): Line[] {
  const out: Line[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^(\S+)[\t,;\s]+(.+)$/);
    if (!m) continue;
    const ref = m[1].replace(/[,;]$/, '').trim();
    if (!/\d/.test(ref)) continue;
    out.push({ ref, date: m[2].trim() });
  }
  return out;
}

const dmy = (iso?: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}-${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1]}-${y}`;
};

/**
 * Orders → Correct booking dates. For shipments an old import dated wrongly (11-09 read as
 * 9 November): paste the LR / AWB with the right date, check the preview, apply.
 */
export function CorrectBookingDatesDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const lines = useMemo(() => parseLines(text), [text]);

  const run = useMutation({
    mutationFn: async (apply: boolean) => (await api.post<Result>('/orders/correct-booking-dates', { lines, apply })).data,
    onSuccess: (r) => {
      setResult(r);
      if (r.apply) {
        toast.success(`${r.changed} booking date(s) corrected`);
        qc.invalidateQueries({ queryKey: ['orders'] });
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not check the dates'),
  });

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Paste one shipment per line: <b>LR / AWB</b> and the correct <b>booking date</b> — copied straight from Excel is fine.
        Dates are read <b>day first</b> (11-Sep-26, 11-09-2026 and 2026-09-11 all mean 11 September). The shipment's
        not-yet-invoiced freight moves to the same date; freight already on an invoice is left as it is.
      </p>
      <textarea value={text} onChange={(e) => { setText(e.target.value); setResult(null); }} rows={8}
        placeholder={'500641485398\t11-Sep-26\n500641482563\t11-Sep-26'}
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
      <div className="text-xs text-slate-500">{lines.length} line(s) read</div>

      {result && (
        <div className="border border-slate-200 rounded-lg">
          <div className="px-3 py-2 text-sm bg-slate-50 border-b border-slate-200 flex flex-wrap gap-x-4">
            {result.apply
              ? <span className="text-emerald-700 font-medium">{result.changed} corrected</span>
              : <span className="font-medium">{result.toChange} will change</span>}
            <span className="text-slate-500">{result.unchanged} already correct</span>
            {result.failed > 0 && <span className="text-rose-600">{result.failed} not possible</span>}
          </div>
          <div className="max-h-[40vh] overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 bg-white sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left">LR / AWB</th>
                  <th className="px-2 py-1.5 text-left">Merchant</th>
                  <th className="px-2 py-1.5 text-left">Now</th>
                  <th className="px-2 py-1.5 text-left">Correct</th>
                  <th className="px-2 py-1.5 text-left">Note</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className={`border-t ${!r.ok ? 'bg-rose-50' : ''}`}>
                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.ref}</td>
                    <td className="px-2 py-1.5">{r.merchant ?? '—'}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap">{dmy(r.before)}</td>
                    <td className="px-2 py-1.5 whitespace-nowrap font-medium">{r.ok ? dmy(r.after) : '—'}</td>
                    <td className="px-2 py-1.5">
                      {!r.ok ? <span className="text-rose-700">{r.error}</span>
                        : r.unchanged ? <span className="text-slate-400">already correct</span>
                        : (
                          <span className="text-slate-600">
                            {r.freightMoved ? 'freight moves too' : 'no unbilled freight'}
                            {r.freightInvoiced?.length ? ` · freight on ${r.freightInvoiced.join(', ')} left as is` : ''}
                            {r.periodInvoice ? <span className="text-amber-700"> · {r.periodInvoice} already covers this date — recalc it to include this LR</span> : ''}
                          </span>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">Close</button>
        <button onClick={() => run.mutate(false)} disabled={!lines.length || run.isPending}
          className="px-4 py-2 border border-primary text-primary text-sm rounded-lg disabled:opacity-50 inline-flex items-center gap-2">
          {run.isPending && !run.variables && <Loader2 size={14} className="animate-spin" />} Preview
        </button>
        <button onClick={() => { if (confirm(`Correct ${result?.toChange ?? 0} booking date(s)?`)) run.mutate(true); }}
          disabled={!result || result.apply || result.toChange === 0 || run.isPending}
          className="px-4 py-2 bg-primary text-white text-sm rounded-lg disabled:opacity-50 inline-flex items-center gap-2">
          {run.isPending && run.variables && <Loader2 size={14} className="animate-spin" />} Apply
        </button>
      </div>
    </div>
  );
}
